'use client';

import { FetchOrderProgressResponseBody, OrderProgressStage } from 'bff';
import { OrderStatus } from 'database';
import { useTranslations } from 'i11n';
import {
  IconBan,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
} from '@tabler/icons-react';
import { AlertComponent, AlertVariant } from './AlertComponent';
import {
  isActionableProgressStatus,
  ORDER_PROGRESS_NEXT_ACTION_KEY,
  ORDER_PROGRESS_WAITING_ON_LABEL_KEY,
  OrderProgressActionableStatus,
  OrderProgressActionAudience,
} from '../constants/order-progress';

interface Outcome {
  variant: AlertVariant;
  icon: React.ReactNode;
  key: string;
  // De qué etapa sale el motivo. `null` cuando el desenlace no tiene motivo.
  reasonStage: OrderProgressStage | null;
}

// Desenlaces terminales: el motivo pesa más que el progreso, así que el callout
// pasa a ser la pieza dominante del encabezado.
const OUTCOME: Record<OrderStatus, Outcome | null> = {
  pending: null,
  in_progress: null,
  completed: {
    variant: 'success',
    icon: <IconCircleCheck className='h-5 w-5' />,
    key: 'outcome.completed',
    reasonStage: null,
  },
  rejected: {
    variant: 'danger',
    icon: <IconCircleX className='h-5 w-5' />,
    key: 'outcome.rejected',
    reasonStage: 'accepted',
  },
  canceled: {
    variant: 'muted',
    icon: <IconBan className='h-5 w-5' />,
    key: 'outcome.canceled',
    reasonStage: 'completed',
  },
};

// Con qué tono se pinta "lo que falta": ámbar si le toca al proveedor, celeste
// si le toca a la aseguradora o corre por fuera, rojo si hay que rehacer algo.
const NEXT_ACTION_VARIANT: Record<OrderProgressActionableStatus, AlertVariant> =
  {
    awaiting_provider: 'warning',
    awaiting_insurer: 'info',
    in_progress: 'info',
    rejected: 'danger',
  };

/**
 * La frase que responde "¿qué falta ahora?". Es el único texto del encabezado
 * que cambia según el lado que mira; el flujo en sí es idéntico para ambos.
 */
export const OrderProgressCallout = ({
  progress,
  audience,
}: {
  progress: FetchOrderProgressResponseBody;
  audience: OrderProgressActionAudience;
}) => {
  const t = useTranslations('orders.progress');

  const outcome = OUTCOME[progress.order_status];

  if (outcome) {
    const reason = progress.stages.find(
      (stage) => stage.stage === outcome.reasonStage,
    )?.reason;

    return (
      <AlertComponent
        className='py-1.5'
        variant={outcome.variant}
        // icon={outcome.icon}
        hideIcon
        title={<span className='text-sm'>{t(outcome.key)}</span>}
        // description={
        //   reason ? <span className='text-[13px]'>«{reason}»</span> : undefined
        // }
      />
    );
  }

  const next = progress.next_action;
  const current = progress.stages.find((item) => item.stage === next?.stage);
  const stage = current?.stage;
  const status = current?.status;

  // `created` nunca es accionable, así que queda fuera de la tabla de copy.
  const copy =
    stage && stage !== 'created' && status && isActionableProgressStatus(status)
      ? ORDER_PROGRESS_NEXT_ACTION_KEY[stage][status]
      : null;

  if (!next || !copy || !status || !isActionableProgressStatus(status)) {
    return (
      <AlertComponent
        className='py-1.5'
        variant='neutral'
        // icon={<IconInfoCircle className='h-5 w-5' />}
        hideIcon
        title={<span className='text-sm'>{t('outcome.idle')}</span>}
      />
    );
  }

  return (
    <AlertComponent
      className='py-1.5'
      variant={NEXT_ACTION_VARIANT[status]}
      // icon={<IconInfoCircle className='h-5 w-5' />}
      hideIcon
      title={
        // Un refetch tras una mutación anuncia el nuevo "qué falta" sin que
        // haya cambiado la página.
        <span className='text-sm' aria-live='polite'>
          {t(copy[audience])}
        </span>
      }
      // description={
      //   <span className='text-[13px]'>
      //     {t(ORDER_PROGRESS_WAITING_ON_LABEL_KEY[next.waiting_on])}
      //   </span>
      // }
    />
  );
};
